import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useRetryCountdown } from "@/hooks/useRetryCountdown";
import { BLOG_CMS_COPY, unauthorisedBody } from "@/lib/blog-cms/copy";
import type { TicketFailure } from "@/lib/blog-cms/protocol";
import { RetryCountdownText } from "./RetryCountdownText";

export type BlogCmsPanelProps =
  | { state: "loading" }
  | { state: "unauthorised"; role: string }
  | {
      state: "refused";
      failure: TicketFailure | { reason: "reauth-exhausted" };
      onRetry: () => void;
      onSignInAgain: () => void;
    }
  | { state: "unreachable"; onRetry: () => void; onOpenInNewTab: () => void };

const CARD =
  "mx-auto mt-10 flex max-w-md flex-col gap-3 rounded-2xl border border-border bg-card p-6";

/** B1: a dummy EmDash shell, sidebar plus rows, under the sign-in status. */
function LoadingPanel() {
  return (
    <div className="flex h-full w-full flex-col gap-4 p-6">
      {/* No aria-busy here: it belongs to the frame container, and on a status node it can
          suppress the pre-filled message (RV2.10). */}
      <p role="status" className="text-sm font-medium text-muted-foreground">
        {BLOG_CMS_COPY.signingIn}
      </p>
      <div aria-hidden="true" className="flex min-h-0 flex-1 gap-4">
        <Skeleton className="hidden w-48 motion-reduce:animate-none sm:block" />
        <div className="flex flex-1 flex-col gap-3">
          <Skeleton className="h-8 w-1/3 motion-reduce:animate-none" />
          <Skeleton className="h-6 w-full motion-reduce:animate-none" />
          <Skeleton className="h-6 w-full motion-reduce:animate-none" />
          <Skeleton className="h-6 w-2/3 motion-reduce:animate-none" />
        </div>
      </div>
    </div>
  );
}

function RateLimitedPanel({ seconds, onRetry }: { seconds: number; onRetry: () => void }) {
  const secondsLeft = useRetryCountdown(seconds);
  return (
    <section className={CARD}>
      <div role="alert">
        <h2 className="text-lg font-semibold">{BLOG_CMS_COPY.rateLimitedTitle}</h2>
        <p className="text-sm text-muted-foreground">{BLOG_CMS_COPY.rateLimitedBody}</p>
      </div>
      <RetryCountdownText secondsLeft={secondsLeft} />
      <div>
        <Button variant="outline" onClick={onRetry} disabled={secondsLeft > 0}>
          {BLOG_CMS_COPY.retry}
        </Button>
      </div>
    </section>
  );
}

function FailurePanel(props: {
  title: string;
  body: string;
  onRetry: () => void;
  secondary?: { label: string; onClick: () => void } | undefined;
}) {
  return (
    <section className={CARD}>
      <div role="alert">
        <h2 className="text-lg font-semibold">{props.title}</h2>
        <p className="text-sm text-muted-foreground">{props.body}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={props.onRetry}>
          {BLOG_CMS_COPY.retry}
        </Button>
        {props.secondary && (
          <Button variant="outline" onClick={props.secondary.onClick}>
            {props.secondary.label}
          </Button>
        )}
      </div>
    </section>
  );
}

/**
 * The non-editor states of `/blog-cms` (B1, B3, B4, B5). Headings are `h2`: the page owns the one
 * `h1` (RV2.8). Every button is `outline`, because the house `default` variant fails contrast
 * (M1-9).
 */
export function BlogCmsStatePanel(props: BlogCmsPanelProps) {
  switch (props.state) {
    case "loading":
      return <LoadingPanel />;
    case "unauthorised":
      return (
        <section className={CARD}>
          <h2 className="text-lg font-semibold">{BLOG_CMS_COPY.unauthorisedTitle}</h2>
          <p className="text-sm text-muted-foreground">{unauthorisedBody(props.role)}</p>
        </section>
      );
    case "unreachable":
      return (
        <FailurePanel
          title={BLOG_CMS_COPY.unreachableTitle}
          body={BLOG_CMS_COPY.unreachableBody}
          onRetry={props.onRetry}
          secondary={{ label: BLOG_CMS_COPY.openInNewTab, onClick: props.onOpenInNewTab }}
        />
      );
    case "refused": {
      const { failure } = props;
      if (failure.reason === "rate-limited") {
        return <RateLimitedPanel seconds={failure.retryAfterSeconds} onRetry={props.onRetry} />;
      }
      if (failure.reason === "reauth-exhausted") {
        return (
          <FailurePanel
            title={BLOG_CMS_COPY.reauthExhaustedTitle}
            body={BLOG_CMS_COPY.reauthExhaustedBody}
            onRetry={props.onRetry}
            secondary={{ label: BLOG_CMS_COPY.signInAgain, onClick: props.onSignInAgain }}
          />
        );
      }
      return (
        <FailurePanel
          title={BLOG_CMS_COPY.failed}
          body={BLOG_CMS_COPY.failedBody}
          onRetry={props.onRetry}
        />
      );
    }
  }
}
